import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { LuArrowLeft, LuUsers, LuMessageSquare, LuUser, LuBot, LuZap, LuZapOff, LuSend, LuClock, LuCircleX, LuPaperclip, LuFileText, LuImage, LuTrash2, LuCheck, LuCheckCheck, LuX, LuLoader, LuUserX, LuStar } from "react-icons/lu";
import { API } from "../App";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const isUnreadConv = (c) => {
  if (!c.last_user_message_at) return false;
  if (Date.now() - new Date(c.last_user_message_at).getTime() >= 24 * 3600000) return false;
  if (!c.read_at) return true;
  return new Date(c.last_user_message_at) > new Date(c.read_at);
};

const STATUS_FILTERS = [
  { value: "all", label: "Todas" },
  { value: "unread", label: "No leídas" },
  { value: "favorite", label: "Favoritas" },
  { value: "handover", label: "Interviniendo" },
  { value: "disengaged", label: "Descualificadas" },
  { value: "opted_out", label: "Opt-out" },
];

const MsgStatusIcon = ({ status }) => {
  if (!status) return null;
  if (status === "sending") return <LuLoader className="w-3 h-3 text-primary-foreground/50 animate-spin" />;
  if (status === "sent")     return <LuCheck className="w-3 h-3 text-primary-foreground/60" title="Enviado" />;
  if (status === "delivered") return <LuCheckCheck className="w-3 h-3 text-primary-foreground/70" title="Entregado" />;
  if (status === "read")     return <LuCheckCheck className="w-3 h-3 text-blue-300" title="Leído" />;
  if (status === "failed")   return <LuX className="w-3 h-3 text-red-400" title="Fallido" />;
  return null;
};

const ConversationsPage = ({ auth }) => {
  const { agentId } = useParams();
  const [agent, setAgent] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const selectedConvIdRef = useRef(null);
  const hasAutoSelectedRef = useRef(false);
  const messagesEndRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [convLimit, setConvLimit] = useState(100);
  const [hasMoreConvs, setHasMoreConvs] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000); // Auto-refresh every 5s
    return () => clearInterval(interval);
  }, [agentId, convLimit]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedConv?.id, selectedConv?.messages?.length]);

  const fetchData = async () => {
    try {
      const [agentRes, convsRes] = await Promise.all([
        fetch(`${API}/agents/${agentId}`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        }),
        fetch(`${API}/conversations/${agentId}?limit=${convLimit}`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        })
      ]);

      if (agentRes.ok) {
        setAgent(await agentRes.json());
      }
      if (convsRes.ok) {
        const data = await convsRes.json();
        setHasMoreConvs(data.length === convLimit);
        const sorted = [...data].sort((a, b) => {
          if (!!a.favorite !== !!b.favorite) return a.favorite ? -1 : 1;
          if (a.handover_active !== b.handover_active) return a.handover_active ? -1 : 1;
          if (isUnreadConv(a) !== isUnreadConv(b)) return isUnreadConv(a) ? -1 : 1;
          return new Date(b.updated_at) - new Date(a.updated_at);
        });
        setConversations(sorted);
        if (sorted.length > 0 && !selectedConvIdRef.current && !hasAutoSelectedRef.current) {
          setSelectedConv(sorted[0]);
          selectedConvIdRef.current = sorted[0].id;
          hasAutoSelectedRef.current = true;
        } else if (selectedConvIdRef.current) {
          const updated = sorted.find(c => c.id === selectedConvIdRef.current);
          if (updated) setSelectedConv(updated);
        }
      }
    } catch (error) {
      // toast.error("Error al cargar conversaciones");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMoreConvs = () => {
    setLoadingMore(true);
    setConvLimit((prev) => prev + 100);
  };

  const handleDeleteConv = async (convId) => {
    try {
      const res = await fetch(`${API}/conversations/${convId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      if (res.ok) {
        toast.success("Conversación eliminada");
        setConversations((prev) => prev.filter((c) => c.id !== convId));
        if (selectedConvIdRef.current === convId) {
          setSelectedConv(null);
          selectedConvIdRef.current = null;
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al eliminar");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handleSelectConv = async (conv) => {
    setSelectedConv(conv);
    selectedConvIdRef.current = conv.id;
    // Marcar como leída si tiene mensajes nuevos sin leer
    const hasUnread = conv.last_user_message_at && (
      !conv.read_at || new Date(conv.last_user_message_at) > new Date(conv.read_at)
    );
    if (hasUnread) {
      try {
        await fetch(`${API}/conversations/${conv.id}/mark-read`, {
          method: "POST",
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        // Actualizar localmente sin esperar al próximo fetchData
        setConversations((prev) =>
          prev.map((c) =>
            c.id === conv.id ? { ...c, read_at: new Date().toISOString() } : c
          )
        );
      } catch { /* no bloquear la UI si falla */ }
    }
  };

  const handleToggleFavorite = async (conv, e) => {
    e?.stopPropagation();
    const newFavorite = !conv.favorite;
    try {
      const res = await fetch(`${API}/conversations/${conv.id}/favorite`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ is_favorite: newFavorite })
      });
      if (res.ok) {
        setConversations((prev) =>
          prev.map((c) => (c.id === conv.id ? { ...c, favorite: newFavorite } : c))
        );
        setSelectedConv((prev) => (prev?.id === conv.id ? { ...prev, favorite: newFavorite } : prev));
      } else {
        toast.error("Error al marcar como favorita");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const handleToggleHandover = async () => {
    if (!selectedConv) return;
    const newState = !selectedConv.handover_active;
    try {
      const res = await fetch(`${API}/conversations/${selectedConv.id}/handover`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ is_active: newState })
      });
      if (res.ok) {
        toast.success(newState ? "Intervención humana activada" : "IA reactivada");
        fetchData();
      }
    } catch (error) {
      toast.error("Error al cambiar modo");
    }
  };

  const [messageText, setMessageText] = useState("");
  const [sendingMedia, setSendingMedia] = useState(false);
  const fileInputRef = useRef(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const handleSendMedia = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConv) return;
    setSendingMedia(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/conversations/${selectedConv.id}/send-media`, {
        method: "POST",
        headers: { Authorization: `Bearer ${auth.token}` },
        body: formData,
      });
      if (res.ok) {
        toast.success(`Archivo enviado: ${file.name}`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al enviar el archivo");
      }
    } catch {
      toast.error("Error de conexión al enviar el archivo");
    } finally {
      setSendingMedia(false);
      e.target.value = "";
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConv) return;
    try {
      const res = await fetch(`${API}/conversations/${selectedConv.id}/respond`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ message: messageText, agent_id: agentId })
      });
      if (res.ok) {
        setMessageText("");
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al enviar mensaje");
        fetchData(); // recargar para mostrar el mensaje fallido en el chat
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, "d MMM, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filteredConversations = conversations.filter((conv) => {
    switch (statusFilter) {
      case "unread": if (!isUnreadConv(conv)) return false; break;
      case "favorite": if (!conv.favorite) return false; break;
      case "handover": if (!conv.handover_active) return false; break;
      case "disengaged": if (!conv.disengaged) return false; break;
      case "opted_out": if (conv.opted_in) return false; break;
      default: break;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const qDigits = q.replace(/\D/g, "");
      const nameMatch = conv.lead_name?.toLowerCase().includes(q);
      const phoneMatch = qDigits && conv.phone_number?.replace(/\D/g, "").includes(qDigits);
      if (!nameMatch && !phoneMatch) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="rounded-xl" data-testid="back-btn">
              <LuArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <LuUsers className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-foreground">Conversaciones</h1>
              <p className="text-xs text-muted-foreground">{agent?.name}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {conversations.length === 0 ? (
          <Card className="glass-card border-white/10 border-dashed">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <LuMessageSquare className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                Sin conversaciones
              </h3>
              <p className="text-muted-foreground max-w-sm mx-auto">
                Las conversaciones de WhatsApp aparecerán aquí cuando los clientes interactúen con tu agente
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
            {/* Conversation List */}
            <div className={`${selectedConv ? "hidden lg:block" : ""} glass-card rounded-2xl border border-white/10 overflow-hidden`}>
              <div className="p-4 border-b border-border space-y-3">
                <div>
                  <h2 className="font-heading font-semibold text-foreground">Chats</h2>
                  <p className="text-xs text-muted-foreground">
                    {filteredConversations.length === conversations.length
                      ? `${conversations.length} conversaciones${hasMoreConvs ? "+" : ""}`
                      : `${filteredConversations.length} de ${conversations.length} conversaciones${hasMoreConvs ? "+" : ""}`}
                  </p>
                </div>
                <input
                  type="text"
                  placeholder="Buscar por nombre o teléfono..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-9 bg-background border border-white/10 rounded-lg px-3 text-sm outline-none focus:border-primary transition-all"
                />
                <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1">
                  {STATUS_FILTERS.map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setStatusFilter(f.value)}
                      className={`shrink-0 text-xs px-2.5 py-1 rounded-full border transition-all ${
                        statusFilter === f.value
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-white/10 text-muted-foreground hover:bg-muted/50"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-[calc(100%-150px)] overflow-y-auto overflow-x-hidden">
                <div className="p-2 space-y-1">
                  {filteredConversations.length === 0 && (
                    <p className="text-center text-sm text-muted-foreground py-8">
                      Ninguna conversación coincide con el filtro
                    </p>
                  )}
                  {filteredConversations.map((conv) => {
                    const unread = conv.last_user_message_at && (
                      !conv.read_at || new Date(conv.last_user_message_at) > new Date(conv.read_at)
                    ) && Date.now() - new Date(conv.last_user_message_at).getTime() < 24 * 3600000;
                    const diff = conv.last_user_message_at
                      ? Date.now() - new Date(conv.last_user_message_at).getTime()
                      : null;
                    const hoursLeft = diff !== null ? Math.max(0, 24 - diff / 3600000) : null;

                    return (
                      <div key={conv.id}>
                        {confirmDeleteId === conv.id ? (
                          <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-between gap-2">
                            <p className="text-xs text-red-400 font-medium">¿Eliminar conversación?</p>
                            <div className="flex gap-1 shrink-0">
                              <button
                                onClick={() => handleDeleteConv(conv.id)}
                                className="text-xs px-2 py-1 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                              >
                                Sí
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="text-xs px-2 py-1 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                              >
                                No
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleSelectConv(conv)}
                            className={`w-full text-left p-3 rounded-xl transition-all ${selectedConv?.id === conv.id ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/50"}`}
                            data-testid={`conv-${conv.id}`}
                          >
                            <div className={`flex items-center gap-2 ${conv.disengaged ? "opacity-60" : ""}`}>
                              <div className="relative shrink-0">
                                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                                  <LuUser className="w-4 h-4 text-muted-foreground" />
                                </div>
                                {unread && (
                                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-background" title="Mensaje sin leer" />
                                )}
                                {conv.disengaged && (
                                  <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-slate-500 border-2 border-background flex items-center justify-center" title="Descualificado">
                                    <LuUserX className="w-2 h-2 text-white" />
                                  </span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <p className="font-medium text-foreground truncate text-sm">
                                    {conv.lead_name || conv.phone_number}
                                  </p>
                                  {conv.handover_active && (
                                    <LuZap className="w-3 h-3 text-amber-500 shrink-0 animate-pulse" />
                                  )}
                                  {conv.disengaged && (
                                    <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30 text-[10px] px-1.5 py-0 shrink-0">
                                      Descualificado
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                  {conv.lead_name && <span className="text-muted-foreground/70">{conv.phone_number} · </span>}
                                  {conv.messages?.[conv.messages.length - 1]?.content || "Sin mensajes"}
                                </p>
                              </div>
                              <div className="flex flex-col items-center gap-1 shrink-0">
                                <span
                                  onClick={(e) => handleToggleFavorite(conv, e)}
                                  className={`p-1 rounded-md transition-all ${conv.favorite ? "text-amber-400 hover:text-amber-500" : "text-muted-foreground hover:text-amber-400 hover:bg-amber-500/20"}`}
                                  title={conv.favorite ? "Quitar de favoritas" : "Marcar como favorita"}
                                >
                                  <LuStar className="w-3.5 h-3.5" fill={conv.favorite ? "currentColor" : "none"} />
                                </span>
                                <span
                                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(conv.id); }}
                                  className="p-1 rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/20 transition-all"
                                  title="Eliminar"
                                >
                                  <LuTrash2 className="w-3.5 h-3.5" />
                                </span>
                                <Badge variant="secondary" className="text-xs px-1.5">
                                  {conv.messages?.length || 0}
                                </Badge>
                                {!conv.opted_in && <LuCircleX className="w-3 h-3 text-red-400" />}
                                {hoursLeft === 0 && <LuClock className="w-3 h-3 text-red-400" />}
                                {hoursLeft > 0 && hoursLeft < 4 && <LuClock className="w-3 h-3 text-amber-400" />}
                              </div>
                            </div>
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {hasMoreConvs && (
                    <button
                      onClick={handleLoadMoreConvs}
                      disabled={loadingMore}
                      className="w-full text-center p-3 rounded-xl text-sm text-primary hover:bg-muted/50 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loadingMore ? (
                        <LuLoader className="w-4 h-4 animate-spin" />
                      ) : null}
                      {loadingMore ? "Cargando..." : "Cargar más conversaciones"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Conversation Detail */}
            <div className={`${!selectedConv ? "hidden lg:flex" : "flex"} lg:col-span-2 glass-card rounded-2xl border border-white/10 overflow-hidden flex-col`}>
              {selectedConv ? (
                <>
                  <div className="p-4 border-b border-border flex flex-wrap items-center gap-3">
                    <Button variant="ghost" size="icon" className="rounded-xl shrink-0 lg:hidden -ml-1" onClick={() => { setSelectedConv(null); selectedConvIdRef.current = null; }}>
                      <LuArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <LuUser className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{selectedConv.lead_name || selectedConv.phone_number}</p>
                      {selectedConv.lead_name && (
                        <p className="text-xs text-muted-foreground">{selectedConv.phone_number}</p>
                      )}
                      {selectedConv.lead_extra_fields && Object.keys(selectedConv.lead_extra_fields).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(selectedConv.lead_extra_fields).map(([label, value]) => (
                            <Badge key={label} variant="secondary" className="text-[10px] px-1.5 font-normal" title={label}>
                              {label}: {value}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-muted-foreground">{formatDate(selectedConv.updated_at)}</p>
                        {!selectedConv.opted_in && (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                            <LuCircleX className="w-2.5 h-2.5 mr-1" />Opt-out
                          </Badge>
                        )}
                        {selectedConv.disengaged && (
                          <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30 text-xs">
                            <LuUserX className="w-2.5 h-2.5 mr-1" />Descualificado
                          </Badge>
                        )}
                        {selectedConv.last_user_message_at && (() => {
                          const diff = Date.now() - new Date(selectedConv.last_user_message_at).getTime();
                          const hoursLeft = Math.max(0, 24 - diff / 3600000);
                          if (hoursLeft === 0) return (
                            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                              <LuClock className="w-2.5 h-2.5 mr-1" />Ventana cerrada
                            </Badge>
                          );
                          if (hoursLeft < 4) return (
                            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                              <LuClock className="w-2.5 h-2.5 mr-1" />{hoursLeft.toFixed(0)}h restantes
                            </Badge>
                          );
                          return null;
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                      <Button
                        variant="outline"
                        size="icon"
                        className={`rounded-xl shrink-0 ${selectedConv.favorite ? "text-amber-400 border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/20" : ""}`}
                        onClick={(e) => handleToggleFavorite(selectedConv, e)}
                        title={selectedConv.favorite ? "Quitar de favoritas" : "Marcar como favorita"}
                      >
                        <LuStar className="w-4 h-4" fill={selectedConv.favorite ? "currentColor" : "none"} />
                      </Button>
                      <Link to={`/leads?phone=${encodeURIComponent(selectedConv.phone_number)}`}>
                        <Button variant="outline" size="sm" className="rounded-xl gap-2" title="Ver ficha del lead">
                          <LuUser className="w-4 h-4" />
                          <span className="hidden sm:inline">Ver ficha</span>
                        </Button>
                      </Link>
                      <Button
                        variant={selectedConv.handover_active ? "secondary" : "outline"}
                        size="sm"
                        onClick={handleToggleHandover}
                        className={`rounded-xl gap-2 ${selectedConv.handover_active ? "bg-amber-500/20 text-amber-500 hover:bg-amber-500/30" : ""}`}
                      >
                        {selectedConv.handover_active ? (
                          <>
                            <LuZap className="w-4 h-4" />
                            Interviniendo
                          </>
                        ) : (
                          <>
                            <LuZapOff className="w-4 h-4" />
                            Modo IA
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {selectedConv.messages?.map((msg, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div className={`flex items-end gap-2 max-w-[80%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${msg.role === "user" ? "bg-primary/10" : "bg-secondary/20"
                              }`}>
                              {msg.role === "user" ? (
                                <LuUser className="w-4 h-4 text-primary" />
                              ) : (msg.content || "").startsWith("(Humano):") ? (
                                <LuUsers className="w-4 h-4 text-amber-500" />
                              ) : (
                                <LuBot className="w-4 h-4 text-secondary-foreground" />
                              )}
                            </div>
                            <div className={`rounded-2xl px-4 py-3 ${msg.role === "user"
                                ? "bg-primary text-primary-foreground rounded-br-md"
                                : "bg-muted text-foreground rounded-bl-md"
                              }`}>
                              {msg.media_type ? (
                                <div className="flex items-center gap-2">
                                  {msg.media_type === "image" ? (
                                    <LuImage className="w-4 h-4 shrink-0" />
                                  ) : (
                                    <LuFileText className="w-4 h-4 shrink-0" />
                                  )}
                                  <span className="text-sm">
                                    {msg.filename || (msg.content || "").replace("(Humano):", "").replace("📎", "").trim()}
                                  </span>
                                </div>
                              ) : (
                                <p className="text-sm whitespace-pre-wrap">
                                  {(msg.content || "").startsWith("(Humano):") ? msg.content.replace("(Humano):", "").trim() : msg.content}
                                </p>
                              )}
                              <p className={`text-xs mt-1 flex items-center gap-1 ${msg.role === "user" ? "text-primary-foreground/70" : "text-muted-foreground"
                                }`}>
                                {formatDate(msg.timestamp)}
                                {msg.role !== "user" && <MsgStatusIcon status={msg.msg_status} />}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                  {/* Chat Input */}
                  <div className="p-4 border-t border-border bg-muted/20">
                    {selectedConv.handover_active && selectedConv.last_user_message_at && (() => {
                      const msAgo = Date.now() - new Date(selectedConv.last_user_message_at).getTime();
                      const windowClosed = msAgo >= 24 * 3600000;
                      const hoursLeft = Math.max(0, 24 - msAgo / 3600000);
                      if (windowClosed) return (
                        <div className="mb-3 flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-500/30 px-3 py-2">
                          <LuClock className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                          <p className="text-xs text-red-400">
                            <span className="font-semibold">Ventana cerrada.</span> El cliente no ha escrito en más de 24h. Los mensajes libres serán rechazados por Meta. Usa una <span className="font-semibold">plantilla aprobada</span> desde la sección de envío de plantillas.
                          </p>
                        </div>
                      );
                      if (hoursLeft < 4) return (
                        <div className="mb-3 flex items-start gap-2 rounded-xl bg-amber-500/10 border border-amber-500/30 px-3 py-2">
                          <LuClock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-400">
                            La ventana de 24h cierra en <span className="font-semibold">{hoursLeft.toFixed(0)}h</span>. Después solo podrás usar plantillas.
                          </p>
                        </div>
                      );
                      return null;
                    })()}
                    <div className="flex gap-2">
                      {selectedConv.handover_active && (
                        <>
                          <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,audio/*,video/*"
                            onChange={handleSendMedia}
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-xl w-12 h-12 shrink-0"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={sendingMedia}
                            title="Adjuntar archivo"
                          >
                            {sendingMedia
                              ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              : <LuPaperclip className="w-5 h-5" />
                            }
                          </Button>
                        </>
                      )}
                      <input
                        type="text"
                        placeholder={selectedConv.handover_active ? "Escribe como humano..." : "Activa 'Interviniendo' para responder manualmente"}
                        className="flex-1 min-w-0 h-12 bg-background border border-white/10 rounded-xl px-4 text-sm outline-none focus:border-primary transition-all"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                        disabled={!selectedConv.handover_active}
                      />
                      <Button
                        onClick={handleSendMessage}
                        size="icon"
                        className="rounded-xl w-12 h-12"
                        disabled={!messageText.trim() || !selectedConv.handover_active}
                      >
                        <LuSend className="w-5 h-5" />
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2 text-center italic">
                      {selectedConv.handover_active
                        ? "La IA está pausada. Tus mensajes se enviarán directamente a WhatsApp."
                        : "La IA está respondiendo automáticamente. Puedes intervenir arriba."}
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  Selecciona una conversación
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ConversationsPage;
